import React, { useState, useMemo, useCallback } from "react";
import { useApi } from "../apiHelpers";
import { useToast } from "../components/Toast/ToastContext";
import useUserStore from "../stores/useUserStore";
import generateTraceabilityPDF from "./generateTraceabilityPDF";
import dayjs from "dayjs";
import { buttonClass } from '../components/ui/Button';
import { Field } from '../components/ui/Field';
import Select from '../components/ui/Select';
import Tag from '../components/ui/Tag';
import Tooltip from '../components/ui/Tooltip';
import EmptyState from '../components/ui/EmptyState';
import LogoSpinner from '../components/ui/LogoSpinner';
import FadeIn from '../components/ui/FadeIn';
import Appear from '../components/ui/Appear';
import Panel, { StatTile } from '../components/ui/Panel';
import { DataTable } from '../components/table/DataTable';

const BOARD_TYPES = ["AP", "ESC", "BAT", "CHG", "LED", "GPS"];

const BoardCaret = ({ open }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="14"
    height="14"
    fill="currentColor"
    viewBox="0 0 16 16"
    className={`transition-transform duration-200 ${open ? "rotate-90" : ""}`}
  >
    <path fillRule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z" />
  </svg>
);

const boardColumns = [
  {
    id: "expand",
    header: "",
    cell: (info) => (
      <span className="text-on-surface-variant">
        <BoardCaret open={info.table.options.meta.expandedBoards.has(info.row.original.boardId)} />
      </span>
    ),
  },
  { id: "type", header: "Type", cell: (info) => <Tag variant="code">{info.row.original.boardType}</Tag> },
  { id: "uid", header: "Primary UID", cell: (info) => <span className="font-mono text-xs">{info.row.original.primaryUid}</span> },
  {
    id: "serial",
    header: "Serial Number",
    cell: (info) => {
      const board = info.row.original;
      return board.serialNumber !== "-" ? (
        <a href={`/drone/${board.serialNumber}`} className="text-primary hover:underline font-mono text-xs">
          {board.serialNumber}
        </a>
      ) : (
        <span className="text-on-surface-variant">-</span>
      );
    },
  },
  {
    id: "flashed",
    header: "Last Flashed",
    cell: (info) => <span className="text-on-surface-variant">{dayjs(info.row.original.latestTimestamp).format("MM/DD/YY HH:mm")}</span>,
  },
  { id: "ops", header: "Operations", cell: (info) => info.row.original.opsCount },
  {
    id: "status",
    header: "Status",
    cell: (info) => (info.row.original.allSuccess ? <Tag tone="good">All Pass</Tag> : <Tag tone="warn">Has Failures</Tag>),
  },
];

const TraceabilityReport = () => {
  const api = useApi();
  const toast = useToast();
  const user = useUserStore((state) => state.user);

  // Filters - default to past month through today
  const [fromDate, setFromDate] = useState(() => dayjs().subtract(1, "month").format("YYYY-MM-DD"));
  const [toDate, setToDate] = useState(() => dayjs().format("YYYY-MM-DD"));
  const [boardType, setBoardType] = useState("");
  const [stationId, setStationId] = useState("");

  // Data
  const [operations, setOperations] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Expandable rows
  const [expandedBoards, setExpandedBoards] = useState(new Set());

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "500" });
      if (fromDate) params.append("from_date", fromDate);
      if (toDate) params.append("to_date", dayjs(toDate).add(1, "day").format("YYYY-MM-DD"));
      if (boardType) params.append("board_type", boardType);
      if (stationId) params.append("station_id", stationId);

      const data = await api.get(`/flasher/admin/operations?${params}`);
      setOperations(data.operations || []);
      setExpandedBoards(new Set());
    } catch (error) {
      console.error("Error fetching report data:", error);
      toast.error("Failed to load report data");
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate, boardType, stationId]);

  // Group operations by board
  const { boardGroups, totalOps, totalBoards, successCount, successRate, byType, byStation } =
    useMemo(() => {
      if (!operations) return { boardGroups: [], totalOps: 0, totalBoards: 0, successCount: 0, successRate: "0.0", byType: {}, byStation: {} };

      const map = new Map();
      operations.forEach((op) => {
        const key = op.board_id || op.id;
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(op);
      });

      const groups = Array.from(map.entries()).map(([boardId, ops]) => {
        const sorted = [...ops].sort(
          (a, b) => new Date(b.operation_timestamp) - new Date(a.operation_timestamp)
        );
        const latest = sorted[0];
        return {
          boardId,
          boardType: latest.board_type || "-",
          primaryUid: latest.primary_uid || "-",
          serialNumber: latest.drone_serialnumber || "-",
          latestTimestamp: latest.operation_timestamp,
          opsCount: ops.length,
          allSuccess: ops.every((o) => o.success),
          operations: sorted,
        };
      });

      groups.sort((a, b) => new Date(b.latestTimestamp) - new Date(a.latestTimestamp));

      const total = operations.length;
      const success = operations.filter((o) => o.success).length;

      const typeBreakdown = {};
      groups.forEach((g) => {
        typeBreakdown[g.boardType] = (typeBreakdown[g.boardType] || 0) + 1;
      });

      const stationBreakdown = {};
      operations.forEach((op) => {
        const s = op.station_id || "Unknown";
        stationBreakdown[s] = (stationBreakdown[s] || 0) + 1;
      });

      return {
        boardGroups: groups,
        totalOps: total,
        totalBoards: groups.length,
        successCount: success,
        successRate: total > 0 ? ((success / total) * 100).toFixed(1) : "0.0",
        byType: typeBreakdown,
        byStation: stationBreakdown,
      };
    }, [operations]);

  const toggleBoard = (boardId) => {
    setExpandedBoards((prev) => {
      const next = new Set(prev);
      if (next.has(boardId)) next.delete(boardId);
      else next.add(boardId);
      return next;
    });
  };

  const handleExportPDF = async () => {
    if (!operations || operations.length === 0) return;
    setExporting(true);
    try {
      await generateTraceabilityPDF({
        operations,
        filters: { fromDate, toDate, boardType, stationId },
        userName: user ? `${user.firstname} ${user.lastname}` : null,
      });
      toast.success("PDF exported");
    } catch (error) {
      console.error("PDF export error:", error);
      toast.error("Failed to export PDF");
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (date) => dayjs(date).format("MM/DD/YY HH:mm");

  return (
    <div className="space-y-6">
      {/* Filter bar */}
      <Panel label="Report filters">
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs text-on-surface-variant mb-1">From Date</label>
            <Field
              type="date"
              aria-label="From Date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-[9.5rem]"
            />
          </div>
          <div>
            <label className="block text-xs text-on-surface-variant mb-1">To Date</label>
            <Field
              type="date"
              aria-label="To Date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-[9.5rem]"
            />
          </div>
          <div>
            <label className="block text-xs text-on-surface-variant mb-1">Board Type</label>
            <Select
              label="Board Type"
              width="fit"
              value={boardType}
              onChange={setBoardType}
              options={[
                { key: "", label: "All Types" },
                ...BOARD_TYPES.map((type) => ({ key: type, label: type })),
              ]}
            />
          </div>
          <div>
            <label className="block text-xs text-on-surface-variant mb-1">Station</label>
            <Field
              type="text"
              aria-label="Station"
              placeholder="Station ID"
              value={stationId}
              onChange={(e) => setStationId(e.target.value)}
              className="w-[8rem]"
            />
          </div>
          <button
            onClick={fetchReport}
            disabled={loading}
            className={buttonClass('tonal')}
          >
            {loading ? "Loading..." : "Generate Report"}
          </button>
          <Appear as="div" show={!!operations && operations.length > 0}>
            <button
              onClick={handleExportPDF}
              disabled={exporting}
              className={buttonClass('tonal')}
            >
              {exporting ? "Exporting..." : "Export PDF"}
            </button>
          </Appear>
        </div>
      </Panel>

      <FadeIn
        key={loading ? 'loading' : operations === null ? 'prompt' : operations.length === 0 ? 'empty' : 'report'}
        className="space-y-6"
      >
      {/* Loading state */}
      {loading && <LogoSpinner label="Loading the report" />}

      {/* No data prompt */}
      {operations === null && !loading && (
        <EmptyState variant="area">
          Set filters and click <span className="text-primary font-semibold">Generate Report</span> to load data.
        </EmptyState>
      )}

      {/* Empty results */}
      {operations && operations.length === 0 && !loading && (
        <EmptyState variant="area">
          No operations found for the selected filters.
        </EmptyState>
      )}

      {/* Report content */}
      {operations && operations.length > 0 && !loading && (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatTile label="Total Boards" value={totalBoards} tone="accent" />
            <StatTile label="Total Operations" value={totalOps} />
            <StatTile
              label="Success Rate"
              value={`${successRate}%`}
              tone={parseFloat(successRate) >= 95 ? "good" : parseFloat(successRate) >= 80 ? "warn" : "danger"}
            />
            <StatTile label="Successful" value={successCount} tone="good" />
          </div>

          {/* Breakdown tables side by side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Board type breakdown */}
            <Panel title="Boards by Type">
              <div className="space-y-2">
                {Object.entries(byType)
                  .sort((a, b) => b[1] - a[1])
                  .map(([type, count]) => (
                    <div key={type} className="flex justify-between items-center">
                      <span className="px-2 py-1 bg-gray-700 rounded text-xs">{type}</span>
                      <span className="font-mono bg-surface-container-highest px-2 py-1 rounded">{count}</span>
                    </div>
                  ))}
              </div>
            </Panel>

            {/* Station breakdown */}
            <Panel title="Operations by Station">
              <div className="space-y-2">
                {Object.entries(byStation)
                  .sort((a, b) => b[1] - a[1])
                  .map(([station, count]) => (
                    <div key={station} className="flex justify-between items-center">
                      <span className="text-on-surface">{station}</span>
                      <span className="font-mono bg-surface-container-highest px-2 py-1 rounded">{count}</span>
                    </div>
                  ))}
              </div>
            </Panel>
          </div>

          {/* Board detail table */}
          <Panel title="Board Detail" padding="none">
            <DataTable
              data={boardGroups}
              columns={boardColumns}
              getRowId={(b) => b.boardId}
              meta={{ expandedBoards, toggleBoard }}
              rowProps={(b) => ({
                onClick: () => toggleBoard(b.boardId),
                className: "cursor-pointer",
              })}
              renderSubRow={(b) => (expandedBoards.has(b.boardId) ? (
                <div className="py-1">
                  {b.operations.map((op) => (
                    <div key={op.id} className="flex flex-wrap items-center gap-x-6 gap-y-1 px-3 py-2 text-xs text-on-surface-variant">
                      <span className="min-w-[16rem]">
                        {formatDate(op.operation_timestamp)}
                        <span className="mx-2 opacity-40">|</span>
                        {op.station_id}
                        <span className="mx-2 opacity-40">|</span>
                        {op.operator_firstname} {op.operator_lastname?.charAt(0)}.
                      </span>
                      <span className="font-mono">{op.firmware_file || "-"}</span>
                      <span>{op.duration_seconds ? `${op.duration_seconds}s` : "-"}</span>
                      {op.success ? (
                        <Tag tone="good" size="sm">Pass</Tag>
                      ) : (
                        <Tooltip label={op.error_message || ""}>
                          <Tag tone="bad" size="sm">Fail</Tag>
                        </Tooltip>
                      )}
                      <span className="min-w-0 max-w-[200px] truncate text-red-300">{op.error_message || ""}</span>
                    </div>
                  ))}
                </div>
              ) : null)}
              empty={<EmptyState variant="area">No boards.</EmptyState>}
            />
          </Panel>
        </>
      )}
      </FadeIn>
    </div>
  );
};

export default TraceabilityReport;
